/**
 * resourceTree — the pure logic behind the Fleet Editor's Resources rail
 * (ADR-132), kept out of FilesStudio.tsx so it can be unit-tested directly, the
 * same way skillDraft.ts / workflowGraph.ts are.
 *
 * Two jobs, both of which used to be missing and are why folders were
 * unmanageable:
 *
 *   1. build a NESTED tree out of the flat repo-relative path list
 *      GET /api/fleet/tree returns (it lists blobs; git has no directory
 *      objects, so folders are inferred from the paths);
 *   2. validate a new file/folder name before it costs a round trip.
 */

/** The repo-relative root of the shared knowledge base (deps.ResourcesRoot). */
export const RESOURCES_ROOT = 'resources'

/** The placeholder that makes a folder exist in git (handler.GitKeepFile). */
export const GITKEEP = '.gitkeep'

/**
 * Which subtree of the overlay a Files surface is showing (ADR-111 IA-3/IA-7).
 *
 * Two scopes, one component. `resources` is the fleet-level shared knowledge base
 * IA-3 scoped this area to; `agent` is the agent's `activities/` folder, added by
 * IA-7 because the typed facets each edit exactly ONE file kind, which left the
 * `activities/*.py` step bodies an ADR-080 python task runs with no surface.
 *
 * NARROWED to `activities/` (2026-08-13, owner review). The first cut rooted this
 * at `agents/<agent>/`, which re-listed `SOUL.md`, `crons.yaml` and `skills/` —
 * precisely the files Persona, Crons and Skills already own, and precisely the
 * redundancy the IA-3 refinement removed from the global escape hatch. Two ways to
 * edit one file is how the two disagree. `activities/` is the only agent subtree
 * no typed facet owns outright: Workflows owns the YAML in it, nothing owns the
 * `.py` beside it.
 */
export type FilesScopeKind = 'resources' | 'agent'

export interface FilesScope {
  kind: FilesScopeKind
  /** The repo-relative root this surface is rooted at. */
  root: string
  /** Set for `agent` scope — also selects the tree node to read. */
  agent?: string
  /** Short display name for the root row. The full path stays in `title`, but a
   *  rail 18rem wide truncates `agents/<agent>/activities/` to uselessness. */
  label?: string
  /** Extensions this surface must NOT show or create, because another facet owns
   *  that file kind. An EXCLUDE list rather than an allowlist on purpose: a file
   *  type nobody owns yet should appear here (that is what this surface is for),
   *  whereas an allowlist would silently hide it. Lowercase, with the dot. */
  hiddenExtensions?: string[]
}

/** Does `path` end in one of `exts` (case-insensitively)? */
export function hasHiddenExtension(path: string, exts: string[] | undefined): boolean {
  if (!exts || exts.length === 0) return false
  const lower = path.toLowerCase()
  return exts.some(e => lower.endsWith(e.toLowerCase()))
}

export const RESOURCES_SCOPE: FilesScope = { kind: 'resources', root: RESOURCES_ROOT }

/**
 * The agent's activities/ folder, MINUS the workflow YAML.
 *
 * The point of this facet is the reusable python step bodies, which no editor
 * reached. The `.yaml` beside them is a workflow, and the Workflows facet owns it
 * end to end — typed fields, the graph, the copilot, the changeset, findings.
 * Listing it here too would widen the surface for changing workflows rather than
 * narrow it, and would give one artifact two editors that can disagree (owner
 * review, 2026-08-13). Same reasoning that took SOUL.md, crons.yaml and skills/
 * out one iteration earlier — applied to the last file kind that had slipped
 * through, because the folder filter was a PATH filter and a workflow is
 * identified by its extension, not its directory.
 */
export function agentFilesScope(agent: string): FilesScope {
  return {
    kind: 'agent',
    root: `agents/${agent}/activities`,
    agent,
    label: 'activities',
    hiddenExtensions: ['.yaml', '.yml'],
  }
}

export interface ResourceFile {
  path: string
  name: string
}

export interface ResourceDir {
  path: string
  name: string
  dirs: ResourceDir[]
  files: ResourceFile[]
}

/**
 * buildResourceTree nests a flat path list under a single root.
 *
 * A `.gitkeep` entry does its whole job by EXISTING: it puts its folder in the
 * tree and is then dropped from the file list, so an operator never sees the
 * placeholder they did not ask for — only the folder they did.
 *
 * Paths outside the root are ignored rather than an error: the same tree
 * endpoint serves `resources/`, every agent overlay, and config/fleet.yaml, and
 * each Files surface shows exactly one of those subtrees (ADR-111 IA-3/IA-4/IA-7).
 * `rootPath` defaults to `resources` so every pre-IA-7 caller is unchanged.
 */
export function buildResourceTree(paths: string[], rootPath: string = RESOURCES_ROOT): ResourceDir {
  const root: ResourceDir = { path: rootPath, name: rootPath, dirs: [], files: [] }
  const byPath = new Map<string, ResourceDir>([[rootPath, root]])

  const ensureDir = (p: string): ResourceDir => {
    const existing = byPath.get(p)
    if (existing) return existing
    const cut = p.lastIndexOf('/')
    if (cut < 0) return root // defensive: only reachable for a path off the root
    const dir: ResourceDir = { path: p, name: p.slice(cut + 1), dirs: [], files: [] }
    ensureDir(p.slice(0, cut)).dirs.push(dir)
    byPath.set(p, dir)
    return dir
  }

  for (const full of [...new Set(paths)].sort()) {
    if (!full.startsWith(rootPath + '/')) continue
    const cut = full.lastIndexOf('/')
    const dir = ensureDir(full.slice(0, cut))
    const name = full.slice(cut + 1)
    if (name === GITKEEP) continue
    dir.files.push({ path: full, name })
  }
  sortDir(root)
  return root
}

function sortDir(d: ResourceDir): void {
  d.dirs.sort((a, b) => a.name.localeCompare(b.name))
  d.files.sort((a, b) => a.name.localeCompare(b.name))
  d.dirs.forEach(sortDir)
}

/** countFiles counts real files in a subtree — placeholders are already gone. */
export function countFiles(d: ResourceDir): number {
  return d.files.length + d.dirs.reduce((n, x) => n + countFiles(x), 0)
}

/** Every folder path in a subtree, for pre-expanding the rail. */
export function dirPaths(d: ResourceDir): string[] {
  return [d.path, ...d.dirs.flatMap(dirPaths)]
}

/** findDir locates a folder node by path, or null. */
export function findDir(dir: ResourceDir, path: string): ResourceDir | null {
  if (dir.path === path) return dir
  for (const d of dir.dirs) {
    const hit = findDir(d, path)
    if (hit) return hit
  }
  return null
}

/** The parent folder of a repo-relative path, bottoming out at the scope root. */
export function parentDir(path: string, rootPath: string = RESOURCES_ROOT): string {
  const cut = path.lastIndexOf('/')
  if (cut < 0) return rootPath
  const parent = path.slice(0, cut)
  // Never climb above the surface's own root: `agents/analytics/x` has a parent
  // of `agents/analytics`, but `agents/analytics` itself must not resolve to
  // `agents`, which this surface does not show and cannot create into.
  return parent.length < rootPath.length ? rootPath : parent
}

/**
 * Every folder from the root down to `dir`, inclusive — the set that has to be
 * expanded for something inside `dir` to be VISIBLE. Expanding only the leaf is
 * not enough: a collapsed ancestor hides it anyway, which is how a
 * just-proposed file appears to have vanished.
 */
export function ancestorDirs(dir: string, rootPath: string = RESOURCES_ROOT): string[] {
  const segs = dir.split('/')
  const out: string[] = []
  for (let i = 1; i <= segs.length; i++) {
    const p = segs.slice(0, i).join('/')
    if (p === rootPath || p.startsWith(rootPath + '/')) out.push(p)
  }
  return out
}

export interface NewPathCheck {
  path?: string
  error?: string
}

/**
 * validateNewPath mirrors the backend's cleanOverlayPath so a bad name is
 * refused before it costs a round trip. The server stays the authority — it
 * re-validates and holds the resources/ allow-list — this is only fast
 * feedback, never the guarantee.
 */
export function validateNewPath(
  raw: string,
  kind: 'file' | 'folder',
  parent: string,
  existing: Set<string>,
  hiddenExtensions?: string[],
): NewPathCheck {
  const cleaned = raw.trim().replace(/^\/+|\/+$/g, '')
  if (!cleaned) return { error: `Give the ${kind} a name.` }
  if (cleaned.includes('\\')) return { error: 'Use forward slashes, not backslashes.' }
  const segments = cleaned.split('/')
  if (segments.some(s => s === '' || s === '.' || s === '..')) {
    return { error: 'That path has an empty or relative segment.' }
  }
  if (segments.includes(GITKEEP)) {
    return { error: `${GITKEEP} is reserved — it is how an empty folder is stored.` }
  }
  // A file this surface would immediately hide must not be creatable here: it
  // would propose successfully and then never appear, which reads as a failure.
  if (kind === 'file' && hasHiddenExtension(cleaned, hiddenExtensions)) {
    return {
      error: 'A workflow (.yaml) is authored on the Workflows facet, which owns it end to end. '
        + 'This surface is for the files no editor owns — the python step bodies.',
    }
  }
  const path = `${parent}/${cleaned}`
  if (existing.has(path)) return { error: `${path} already exists.` }
  // A name that is already a folder collides whichever kind is being created:
  // git cannot hold a blob and a tree at the same path.
  if ([...existing].some(p => p.startsWith(path + '/'))) {
    return { error: kind === 'folder' ? `${path} already exists.` : `${path} is a folder.` }
  }
  return { path }
}
